import React from 'react';
import { getPartColor } from '../utils/colors';

/**
 * Shared sheet visualization component
 * Used by both desktop and mobile views with different configurations
 *
 * @param {Object} layout - The sheet layout with pieces
 * @param {string} mode - 'interactive' | 'print' | 'guided'
 * @param {string|null} highlightedPieceId - ID of piece to highlight
 * @param {Function} onPieceHover - Callback when piece is hovered
 * @param {Function} onPieceClick - Callback when piece is clicked
 * @param {boolean} showDimensions - Whether to show piece dimensions
 * @param {boolean} highContrast - Use high contrast black/white style
 * @param {number} maxWidth - Maximum width of the rendered SVG
 * @param {boolean} showLabels - Whether to show piece name labels
 */
const SheetRenderer = ({
  layout,
  mode = 'interactive',
  highlightedPieceId = null,
  onPieceHover,
  onPieceClick,
  showDimensions = false,
  highContrast = false,
  maxWidth = 300,
  maxHeight = null,
  showLabels = true,
  numberLabels = false,
  cutSequence = null,
  darkMode = false,
  cutPieceIds = null, // Set of piece IDs that have been cut (for shop sheet interactivity)
  fontScale = 1, // Scale factor for label and dimension fonts
}) => {
  const { width: sheetWidth, length: sheetLength, pieces } = layout;

  // Calculate scale to fit within maxWidth and optionally maxHeight
  // This ensures horizontally long sheets don't break the layout
  let scale = maxWidth / sheetLength;
  if (maxHeight && sheetWidth * scale > maxHeight) {
    scale = maxHeight / sheetWidth;
  }

  const viewWidth = sheetLength * scale;
  const viewHeight = sheetWidth * scale;

  // Build piece-id to part number map when numberLabels is enabled
  // Groups by name+dims+grain so identical parts share a number
  const pieceNumMap = {};
  if (numberLabels && cutSequence) {
    const pieceCuts = cutSequence.filter(c => c.type === 'piece');
    const partKeyToNum = new Map();
    let nextNum = 1;
    pieceCuts.forEach(cut => {
      const p = cut.piece;
      const key = `${p.name}|${p.width.toFixed(4)}|${p.length.toFixed(4)}|${p.grainDirection}`;
      if (!partKeyToNum.has(key)) {
        partKeyToNum.set(key, nextNum++);
      }
    });
    // Now assign to layout pieces
    pieces.forEach(piece => {
      const key = `${piece.name}|${piece.width.toFixed(4)}|${piece.length.toFixed(4)}|${piece.grainDirection}`;
      if (partKeyToNum.has(key)) {
        pieceNumMap[piece.id] = partKeyToNum.get(key);
      }
    });
  }

  const getStyles = (piece, isHighlighted) => {
    if (highContrast) {
      return {
        fill: isHighlighted ? (darkMode ? '#3a3a3a' : '#E5E5E5') : (darkMode ? '#2a2a2a' : '#FFFFFF'),
        stroke: darkMode ? '#666' : '#000000',
        strokeWidth: isHighlighted ? 1.5 : 0.75,
        opacity: 1,
      };
    }

    const baseColor = getPartColor(piece.originalId).hex;
    return {
      fill: baseColor,
      stroke: isHighlighted ? '#000000' : '#000000',
      strokeWidth: isHighlighted ? 2 : 1,
      opacity: isHighlighted ? 1 : 0.8,
    };
  };

  const renderGrainLines = (piece) => {
    if (numberLabels) return null;
    if (piece.grainDirection === 'none') return null;

    const isHorizontalGrain =
      (piece.grainDirection === 'horizontal' && !piece.rotated) ||
      (piece.grainDirection === 'vertical' && piece.rotated);

    const lineColor = highContrast ? '#666666' : 'white';
    const lineOpacity = highContrast ? 1 : 0.7;

    if (isHorizontalGrain) {
      return (
        <>
          <line
            x1={piece.x * scale + 5}
            y1={piece.y * scale + (piece.width * scale) / 3}
            x2={piece.x * scale + piece.length * scale - 5}
            y2={piece.y * scale + (piece.width * scale) / 3}
            stroke={lineColor}
            strokeWidth="1"
            opacity={lineOpacity}
          />
          <line
            x1={piece.x * scale + 5}
            y1={piece.y * scale + (2 * piece.width * scale) / 3}
            x2={piece.x * scale + piece.length * scale - 5}
            y2={piece.y * scale + (2 * piece.width * scale) / 3}
            stroke={lineColor}
            strokeWidth="1"
            opacity={lineOpacity}
          />
        </>
      );
    } else {
      return (
        <>
          <line
            x1={piece.x * scale + (piece.length * scale) / 3}
            y1={piece.y * scale + 5}
            x2={piece.x * scale + (piece.length * scale) / 3}
            y2={piece.y * scale + piece.width * scale - 5}
            stroke={lineColor}
            strokeWidth="1"
            opacity={lineOpacity}
          />
          <line
            x1={piece.x * scale + (2 * piece.length * scale) / 3}
            y1={piece.y * scale + 5}
            x2={piece.x * scale + (2 * piece.length * scale) / 3}
            y2={piece.y * scale + piece.width * scale - 5}
            stroke={lineColor}
            strokeWidth="1"
            opacity={lineOpacity}
          />
        </>
      );
    }
  };

  // Helper: check if two rectangles overlap
  const rectsOverlap = (r1, r2) => {
    if (!r1 || !r2) return false;
    return !(r1.right < r2.left || r1.left > r2.right || r1.bottom < r2.top || r1.top > r2.bottom);
  };

  // Helper: check if bbox fits within piece bounds
  const fitsInPiece = (bbox, piece) => {
    const px = piece.x * scale;
    const py = piece.y * scale;
    const pw = piece.length * scale;
    const ph = piece.width * scale;
    return bbox.left >= px && bbox.right <= px + pw && bbox.top >= py && bbox.bottom <= py + ph;
  };

  // Helper: calculate label info for collision detection and rendering decisions
  const getLabelInfo = (piece) => {
    const pieceWidthPx = piece.length * scale;
    const pieceHeightPx = piece.width * scale;
    
    const labelText = numberLabels && pieceNumMap[piece.id]
      ? (() => { let n = pieceNumMap[piece.id]; let s = ''; while (n > 0) { n--; s = String.fromCharCode(65 + (n % 26)) + s; n = Math.floor(n / 26); } return s; })()
      : piece.name;
    
    const baseFontSize = (highContrast ? 12 : 10) * fontScale;
    const charWidthRatio = 0.6;
    const absoluteMinFontSize = 4 * fontScale; // Ultimate minimum before hiding
    
    // Try to fit, shrinking if needed
    let fontSize = baseFontSize;
    let textWidth = labelText.length * fontSize * charWidthRatio;
    let textHeight = fontSize;
    
    // Shrink to fit width
    if (textWidth > pieceWidthPx - 4) {
      fontSize = Math.max(absoluteMinFontSize, ((pieceWidthPx - 4) / labelText.length) / charWidthRatio);
      textWidth = labelText.length * fontSize * charWidthRatio;
      textHeight = fontSize;
    }
    
    // Shrink to fit height
    if (textHeight > pieceHeightPx - 4) {
      fontSize = Math.max(absoluteMinFontSize, pieceHeightPx - 4);
      textWidth = labelText.length * fontSize * charWidthRatio;
      textHeight = fontSize;
    }
    
    const centerX = piece.x * scale + pieceWidthPx / 2;
    const centerY = piece.y * scale + pieceHeightPx / 2 - (numberLabels ? 0 : (piece.rotated ? 2 : 5));
    
    const bbox = {
      left: centerX - textWidth / 2,
      right: centerX + textWidth / 2,
      top: centerY - textHeight / 2,
      bottom: centerY + textHeight / 2
    };
    
    // Check if label can fit at all (ultimate last resort: hide)
    const canFit = fontSize >= absoluteMinFontSize && fitsInPiece(bbox, piece);
    
    return { labelText, fontSize, textWidth, textHeight, centerX, centerY, bbox, canFit };
  };

  const renderPieceLabel = (piece) => {
    if (!showLabels) return null;

    const textColor = highContrast ? (darkMode ? '#ddd' : '#000000') : 'white';
    const pieceWidthPx = piece.length * scale;
    const pieceHeightPx = piece.width * scale;

    // Use getLabelInfo for consistent sizing with collision detection
    const labelInfo = getLabelInfo(piece);
    
    // Ultimate last resort: hide label only if it truly cannot fit
    if (!labelInfo.canFit) return null;

    const { labelText, fontSize, centerX, centerY } = labelInfo;

    return (
      <>
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fill={textColor}
          fontWeight="bold"
        >
          {labelText}
        </text>
        {!numberLabels && piece.rotated && pieceHeightPx > 30 && fontSize > 6 && (
          <text
            x={centerX}
            y={centerY + Math.min(8, fontSize * 0.8)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={Math.min(highContrast ? 10 : 8, fontSize * 0.8)}
            fill={textColor}
            fontStyle="italic"
          >
            (rotated)
          </text>
        )}
      </>
    );
  };

  const renderCutMark = (piece) => {
    if (!cutPieceIds || !cutPieceIds.has(piece.id)) return null;

    const x = piece.x * scale;
    const y = piece.y * scale;
    const w = piece.length * scale;
    const h = piece.width * scale;
    const padding = Math.min(w, h) * 0.1;

    return (
      <>
        {/* X mark - thin black lines */}
        <line
          x1={x + padding}
          y1={y + padding}
          x2={x + w - padding}
          y2={y + h - padding}
          stroke={darkMode ? '#888' : '#000'}
          strokeWidth={1.5}
        />
        <line
          x1={x + w - padding}
          y1={y + padding}
          x2={x + padding}
          y2={y + h - padding}
          stroke={darkMode ? '#888' : '#000'}
          strokeWidth={1.5}
        />
      </>
    );
  };

  // Snap to 1/128" and strip trailing zeros for tooltips (values are in inches)
  const formatTooltipValue = (value) => {
    const snapped = Math.round(value * 128) / 128;
    return snapped.toFixed(6).replace(/\.?0+$/, '');
  };

  // Format dimension value: up to 3 decimals, trim trailing zeros
  const formatDimension = (value) => {
    return value.toFixed(3).replace(/\.?0+$/, '');
  };

  const renderDimensions = (piece) => {
    if (!showDimensions) return null;

    const textColor = highContrast ? (darkMode ? '#999' : '#000000') : 'white';
    const pieceWidthPx = piece.length * scale;
    const pieceHeightPx = piece.width * scale;
    const pieceX = piece.x * scale;
    const pieceY = piece.y * scale;

    const baseFontSize = 8 * fontScale;
    const minFontSize = 4 * fontScale;
    const charWidthRatio = 0.55;
    const bottomPadding = fontScale > 1 ? 5 : 3;
    const leftPadding = fontScale > 1 ? 5 : 3;

    const widthValue = formatDimension(piece.width);
    const lengthValue = formatDimension(piece.length);

    // Get label bbox for collision detection
    const labelInfo = showLabels ? getLabelInfo(piece) : null;
    const labelBbox = labelInfo?.canFit ? labelInfo.bbox : null;

    // === PHASE 1: Calculate required font sizes for each dimension ===
    
    // Width dimension (left side, rotated) - calculate needed font size
    let widthFontSize = baseFontSize;
    let widthCanShow = true;
    let widthCy = pieceY + pieceHeightPx / 2;
    const widthCx = pieceX + leftPadding;
    
    // Shrink to fit piece height
    const maxWidthTextLength = pieceHeightPx - 6;
    if (widthValue.length * widthFontSize * charWidthRatio > maxWidthTextLength) {
      widthFontSize = Math.max(minFontSize, (maxWidthTextLength / widthValue.length) / charWidthRatio);
    }
    
    // Check collision and potentially shrink more
    if (labelBbox) {
      const getWidthBbox = (fs, y) => {
        const tw = widthValue.length * fs * charWidthRatio;
        const th = fs;
        return { left: widthCx - th/2, right: widthCx + th/2, top: y - tw/2, bottom: y + tw/2 };
      };
      
      let widthBbox = getWidthBbox(widthFontSize, widthCy);
      if (rectsOverlap(labelBbox, widthBbox)) {
        // Try to move up
        const textWidth = widthValue.length * widthFontSize * charWidthRatio;
        const newCy = labelBbox.top - textWidth / 2 - 2;
        if (newCy - textWidth / 2 >= pieceY + 2) {
          widthCy = newCy;
        } else {
          // Need to shrink to fit above label
          const availableHeight = labelBbox.top - pieceY - 4;
          const newFontSize = Math.max(minFontSize, (availableHeight / widthValue.length) / charWidthRatio * 0.8);
          if (newFontSize >= minFontSize) {
            widthFontSize = newFontSize;
            widthCy = pieceY + (widthValue.length * widthFontSize * charWidthRatio) / 2 + 2;
          } else {
            widthCanShow = false;
          }
        }
      }
    }
    
    // Length dimension (bottom, horizontal) - calculate needed font size
    let lengthFontSize = baseFontSize;
    let lengthCanShow = true;
    let lengthLx = pieceX + pieceWidthPx / 2;
    const lengthLy = pieceY + pieceHeightPx - bottomPadding;
    
    // Shrink to fit piece width
    const maxLengthTextLength = pieceWidthPx - 6;
    if (lengthValue.length * lengthFontSize * charWidthRatio > maxLengthTextLength) {
      lengthFontSize = Math.max(minFontSize, (maxLengthTextLength / lengthValue.length) / charWidthRatio);
    }
    
    // Check collision and potentially shrink more
    if (labelBbox) {
      const getLengthBbox = (fs, x) => {
        const tw = lengthValue.length * fs * charWidthRatio;
        const th = fs;
        return { left: x - tw/2, right: x + tw/2, top: lengthLy - th, bottom: lengthLy };
      };
      
      let lengthBbox = getLengthBbox(lengthFontSize, lengthLx);
      if (rectsOverlap(labelBbox, lengthBbox)) {
        // Try to move right
        const textWidth = lengthValue.length * lengthFontSize * charWidthRatio;
        const newLx = labelBbox.right + textWidth / 2 + 2;
        if (newLx + textWidth / 2 <= pieceX + pieceWidthPx - 2) {
          lengthLx = newLx;
        } else {
          // Need to shrink to fit right of label
          const availableWidth = pieceX + pieceWidthPx - labelBbox.right - 4;
          const newFontSize = Math.max(minFontSize, (availableWidth / lengthValue.length) / charWidthRatio * 0.9);
          if (newFontSize >= minFontSize) {
            lengthFontSize = newFontSize;
            lengthLx = pieceX + pieceWidthPx - (lengthValue.length * lengthFontSize * charWidthRatio) / 2 - 2;
          } else {
            lengthCanShow = false;
          }
        }
      }
    }
    
    // === PHASE 2: Use the smaller font size for both dimensions ===
    const finalFontSize = Math.min(widthFontSize, lengthFontSize);
    
    // Recalculate positions with final font size if it changed
    if (finalFontSize < widthFontSize && widthCanShow) {
      // Width got smaller, recalc position
      const textWidth = widthValue.length * finalFontSize * charWidthRatio;
      if (labelBbox) {
        const widthBbox = { 
          left: widthCx - finalFontSize/2, right: widthCx + finalFontSize/2, 
          top: widthCy - textWidth/2, bottom: widthCy + textWidth/2 
        };
        if (rectsOverlap(labelBbox, widthBbox)) {
          widthCy = labelBbox.top - textWidth / 2 - 2;
          if (widthCy - textWidth / 2 < pieceY + 2) {
            widthCy = pieceY + textWidth / 2 + 2;
          }
        }
      }
    }
    
    if (finalFontSize < lengthFontSize && lengthCanShow) {
      // Length got smaller, recalc position
      const textWidth = lengthValue.length * finalFontSize * charWidthRatio;
      if (labelBbox) {
        const lengthBbox = { 
          left: lengthLx - textWidth/2, right: lengthLx + textWidth/2, 
          top: lengthLy - finalFontSize, bottom: lengthLy 
        };
        if (rectsOverlap(labelBbox, lengthBbox)) {
          lengthLx = labelBbox.right + textWidth / 2 + 2;
          if (lengthLx + textWidth / 2 > pieceX + pieceWidthPx - 2) {
            lengthLx = pieceX + pieceWidthPx - textWidth / 2 - 2;
          }
        }
      }
    }

    // === PHASE 3: Render elements ===
    const elements = [];
    
    if (widthCanShow && finalFontSize >= minFontSize) {
      elements.push(
        <text
          key="width"
          x={widthCx}
          y={widthCy}
          textAnchor="middle"
          dominantBaseline="text-bottom"
          fontSize={finalFontSize}
          fill={textColor}
          transform={`rotate(90, ${widthCx}, ${widthCy})`}
        >
          <title>{formatTooltipValue(piece.width)}</title>
          {widthValue}
        </text>
      );
    }

    if (lengthCanShow && finalFontSize >= minFontSize) {
      elements.push(
        <text
          key="length"
          x={lengthLx}
          y={lengthLy}
          textAnchor="middle"
          fontSize={finalFontSize}
          fill={textColor}
        >
          <title>{formatTooltipValue(piece.length)}</title>
          {lengthValue}
        </text>
      );
    }

    return elements.length > 0 ? <>{elements}</> : null;
  };

  return (
    <svg
      width={viewWidth}
      height={viewHeight}
      viewBox={`0 0 ${viewWidth} ${viewHeight}`}
      style={{ maxWidth: '100%', maxHeight: '100%', height: 'auto' }}
      className={`border ${highContrast ? (darkMode ? 'border-[#444]' : (layout.trimAmount > 0 ? 'border-gray-300' : 'border-black')) : 'border-gray-300'}`}
    >
      {/* Sheet background */}
      <rect
        width={viewWidth}
        height={viewHeight}
        fill={darkMode ? '#1e1e1e' : (highContrast ? '#FFFFFF' : '#f8f9fa')}
        stroke={darkMode ? '#444' : (highContrast ? (layout.trimAmount > 0 ? '#dee2e6' : '#000000') : '#dee2e6')}
      />

      {/* Trim area visualization - show trimmed edges */}
      {layout.trimAmount > 0 && (
        <>
          {/* Shaded trim areas on all edges */}
          <rect
            x={0}
            y={0}
            width={viewWidth}
            height={layout.trimAmount * scale}
            fill={darkMode ? '#333' : (highContrast ? '#ccc' : '#e0e0e0')}
            opacity={highContrast ? 0.25 : 0.5}
          />
          <rect
            x={0}
            y={viewHeight - layout.trimAmount * scale}
            width={viewWidth}
            height={layout.trimAmount * scale}
            fill={darkMode ? '#333' : (highContrast ? '#ccc' : '#e0e0e0')}
            opacity={highContrast ? 0.25 : 0.5}
          />
          <rect
            x={0}
            y={layout.trimAmount * scale}
            width={layout.trimAmount * scale}
            height={viewHeight - 2 * layout.trimAmount * scale}
            fill={darkMode ? '#333' : (highContrast ? '#ccc' : '#e0e0e0')}
            opacity={highContrast ? 0.25 : 0.5}
          />
          <rect
            x={viewWidth - layout.trimAmount * scale}
            y={layout.trimAmount * scale}
            width={layout.trimAmount * scale}
            height={viewHeight - 2 * layout.trimAmount * scale}
            fill={darkMode ? '#333' : (highContrast ? '#ccc' : '#e0e0e0')}
            opacity={highContrast ? 0.25 : 0.5}
          />
          {/* Dashed line showing usable area boundary */}
          <rect
            x={layout.trimAmount * scale}
            y={layout.trimAmount * scale}
            width={viewWidth - 2 * layout.trimAmount * scale}
            height={viewHeight - 2 * layout.trimAmount * scale}
            fill="none"
            stroke={highContrast ? '#000' : (darkMode ? '#666' : '#999')}
            strokeWidth={highContrast ? 0.5 : 1}
            strokeDasharray="4,2"
          />
        </>
      )}

      {/* Pieces */}
      {pieces.map((piece) => {
        const isHighlighted = highlightedPieceId === piece.id || highlightedPieceId === piece.originalId;
        const styles = getStyles(piece, isHighlighted);

        // Render polygon for non-rectangular shapes
        const renderPieceShape = () => {
          if (piece.vertices && piece.shapeType !== 'rectangle') {
            // Convert vertices to SVG polygon points
            // Vertices are relative to piece origin, need to add piece.x, piece.y
            const points = piece.vertices.map(v =>
              `${(piece.x + v.x) * scale},${(piece.y + v.y) * scale}`
            ).join(' ');

            return (
              <polygon
                points={points}
                fill={styles.fill}
                stroke={styles.stroke}
                strokeWidth={styles.strokeWidth}
                opacity={styles.opacity}
              />
            );
          }

          // Default rectangle rendering
          return (
            <rect
              x={piece.x * scale}
              y={piece.y * scale}
              width={piece.length * scale}
              height={piece.width * scale}
              fill={styles.fill}
              stroke={styles.stroke}
              strokeWidth={styles.strokeWidth}
              opacity={styles.opacity}
            />
          );
        };

        return (
          <g
            key={piece.id}
            onMouseEnter={() => onPieceHover?.(piece.originalId || piece.id)}
            onMouseLeave={() => onPieceHover?.(null)}
            onClick={() => onPieceClick?.(piece)}
            style={{ cursor: mode === 'interactive' ? 'pointer' : 'default' }}
          >
            {renderPieceShape()}
            {renderGrainLines(piece)}
            {renderPieceLabel(piece)}
            {renderDimensions(piece)}
            {renderCutMark(piece)}
          </g>
        );
      })}
    </svg>
  );
};

/**
 * Sheet card wrapper with title and stats
 */
export const SheetCard = ({
  layout,
  title,
  showStats = true,
  horizontal = false,
  className = '',
  maxWidth,
  maxHeight,
  highlightedPieceId,
  onPieceHover,
  ...rendererProps
}) => {
  const usedArea = layout.pieces.reduce((sum, p) => sum + p.width * p.length, 0);

  // Build parts list grouped by name with quantity and color
  const partsList = [];
  const partCounts = new Map();
  layout.pieces.forEach(piece => {
    const name = piece.name || 'Unnamed';
    if (!partCounts.has(name)) {
      const color = getPartColor(piece.originalId).hex;
      partCounts.set(name, { count: 0, color, originalId: piece.originalId });
    }
    partCounts.get(name).count += 1;
  });
  partCounts.forEach((val, name) => {
    partsList.push({ name, count: val.count, color: val.color, originalId: val.originalId });
  });

  const partsListEl = partsList.length > 0 && (
    <div className="flex flex-wrap justify-start gap-x-2 gap-y-1.5 overflow-hidden">
      {partsList.map((part, i) => {
        const isHovered = highlightedPieceId === part.originalId;
        return (
          <div
            key={i}
            className={`inline-flex items-center gap-1.5 text-xs transition-all cursor-pointer rounded-full px-2.5 py-1 whitespace-nowrap ${
              isHovered
                ? 'text-gray-900 font-semibold'
                : 'text-gray-700 hover:text-gray-900'
            }`}
            style={{
              border: `2px solid ${part.color}`,
              backgroundColor: `${part.color}20`,
            }}
            onMouseEnter={() => onPieceHover?.(part.originalId)}
            onMouseLeave={() => onPieceHover?.(null)}
          >
            <span>{part.name}</span>
            <span className="text-gray-400">×{part.count}</span>
          </div>
        );
      })}
    </div>
  );

  const statsEl = showStats && (
    <div className="text-sm text-gray-600">
      {layout.pieces.length} pieces • {usedArea.toFixed(1)} sq in used
    </div>
  );

  return (
    <div className={`border rounded-xl p-4 bg-white shadow-sm ${className}`}>
      {title && (
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-semibold">{title}</h3>
          {layout.thicknessLabel && (
            <span className="px-2 py-0.5 text-xs bg-sage-100 text-sage-700 rounded-full">
              {layout.thicknessLabel}
            </span>
          )}
        </div>
      )}
      {horizontal ? (
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            <SheetRenderer
              layout={layout}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              showLabels={false}
              highlightedPieceId={highlightedPieceId}
              onPieceHover={onPieceHover}
              {...rendererProps}
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
            {partsListEl}
            {statsEl}
          </div>
        </div>
      ) : (
        <>
          <SheetRenderer
            layout={layout}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            showLabels={false}
            highlightedPieceId={highlightedPieceId}
            onPieceHover={onPieceHover}
            {...rendererProps}
          />
          {partsListEl && <div className="mt-3">{partsListEl}</div>}
          {statsEl && <div className="mt-2">{statsEl}</div>}
        </>
      )}
    </div>
  );
};

export default SheetRenderer;
